import { useState, useEffect } from "react";
import { Spinner } from "lucide-react";
import AdminLayout from "../../../components/admin/AdminLayout";
import EditCategoryModal from "../../../components/admin/EditCategoryModal";
import { PageHeader } from "../../../components/admin/CategoriesPage/PageHeader";
import { FilterBar } from "../../../components/admin/CategoriesPage/FilterBar";
import { BulkActionsBar } from "../../../components/admin/CategoriesPage/BulkActionsBar";
import { EmptyState } from "../../../components/admin/CategoriesPage/EmptyState";
import { CategoriesTable } from "../../../components/admin/CategoriesPage/CategoriesTable";
import { DeleteConfirmModal } from "../../../components/admin/CategoriesPage/DeleteConfirmModal";
import AddCategoryModal from "../../../components/admin/AddCategoryModal/AddCategoryModal"; // Ensure default import
import { LoadingSkeleton } from "../../../components/admin/CategoriesPage/LoadingSkeleton";
import { useCategoryFilters } from "../../../hooks/useCategoryFilters";
import { useCategorySelection } from "../../../hooks/useCategorySelection";
import { useAddCategoryModal } from "../../../hooks/useAddCategoryModal";
import AdminCategoryService from "../../../services/admin/AdminCategoryServices";
import { useNotification } from "../../../context/NotificationContext";
import { validateCategory, validateCategories } from "../../../utils/categoryValidator";
import { useAdminPermissions } from "../../../hooks/useAdminPermissions";

export default function CategoriesPage() {
  const { showNotification } = useNotification();
  const { checkPermission } = useAdminPermissions();
  const [categories, setCategories] = useState([]);
  const [unitMeasurements, setUnitMeasurements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [loadingStatus, setLoadingStatus] = useState({});
  const [loadingDelete, setLoadingDelete] = useState({});


  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);


  // Add category modal state
  const {
    newCategories,
    setNewCategories,
    handleImageUpload,
    addAnotherCategory,
    removeCategory,
    resetCategories,
    getValidCategories,
  } = useAddCategoryModal();


  // Selection & filtering
  const {
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    sortBy,
    setSortBy,
    filteredCategories,
  } = useCategoryFilters(categories);


  const {
    selectedCategories,
    setSelectedCategories,
    handleSelectAll,
    handleSelectCategory,
  } = useCategorySelection();


  const allSelected =
    filteredCategories.length > 0 &&
    selectedCategories.length === filteredCategories.length;


  const normalizeCategory = (cat) => ({
    ...cat,
    productsCount:
      cat.productsCount ??
      cat.products_count ??
      0,

    unitMeasurement:
      cat.unitMeasurement ??
      cat.unit_measurement ??
      "",

  });

  const updateUnitMeasurementsState = (unitsToAdd) => {
    setUnitMeasurements((prev) => {
      let updated = false;
      const newUnits = [...prev];
      unitsToAdd.forEach((unit) => {
        if (unit && unit !== "Custom" && !newUnits.includes(unit)) {
          const customIndex = newUnits.indexOf("Custom");
          if (customIndex !== -1) {
            newUnits.splice(customIndex, 0, unit);
          } else {
            newUnits.push(unit);
          }
          updated = true;
        }
      });
      return updated ? newUnits : prev;
    });
  };




  // Fetch categories & unit measurements on mount
  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [categoryList, units] = await Promise.all([
          AdminCategoryService.list(),
          AdminCategoryService.getUnitMeasurements(),
        ]);

        if (mounted) {
          setCategories(categoryList);
          setUnitMeasurements(units);
        }
      } catch (err) {
        console.error("Failed to fetch categories or units:", err);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    fetchData();
    return () => (mounted = false);
  }, []);

  // Add new categories
  const handleAddCategorySubmit = async () => {
    if (!checkPermission("admin", "create categories")) return;

    // 🎨 Frontend Validation using the shared validator
    const validationErrors = validateCategories(newCategories);

    if (validationErrors.length > 0) {
      // Show descriptive notifications for each invalid row
      validationErrors.forEach(({ index, errors }) => {
        const errorMessages = Object.values(errors).join(", ");
        showNotification({
          type: "error",
          title: `Validation Error (Item ${index + 1})`,
          message: errorMessages,
          duration: 5000,
        });
      });
      return; // Stop submission
    }

    const validCategories = getValidCategories();
    if (validCategories.length === 0) return;

    showNotification({
      type: "info",
      title: "Saving Categories",
      message: "Adding new categories, please wait...",
    });

    try {
      const response = await AdminCategoryService.create(validCategories);

      const createdRaw = Array.isArray(response?.data)
        ? response.data
        : [];

      const errors = Array.isArray(response?.errors) ? response.errors : [];

      // 🔥 Normalize shape to match existing categories
      const createdNormalized = createdRaw.map((cat) => ({
        ...cat,
        unitMeasurement:
          typeof cat.unitMeasurement === "object"
            ? cat.unitMeasurement?.name
            : cat.unitMeasurement,
      }));

      if (createdNormalized.length > 0) {
        setCategories((prev) => [...createdNormalized, ...prev]);
        updateUnitMeasurementsState(createdNormalized.map(cat => cat.unitMeasurement));
        showNotification({
          type: "success",
          title: "Categories Created",
          message: `Successfully added ${createdNormalized.length} categories.`,
        });
      }

      if (errors.length > 0) {
        errors.forEach((err) => {
          showNotification({
            type: "error",
            title: `Error at Index ${err.index}`,
            message: err.message,
            duration: 6000,
          });
        });
      }

      if (createdNormalized.length > 0) {
        resetCategories();
        setShowAddModal(false);
      }
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to add categories";
      showNotification({
        type: "error",
        title: "Submission Error",
        message: message,
      });
      console.error(
        "Failed to add categories:",
        err?.response?.data || err
      );
    }
  };


  // Edit category
  const handleEditSaveWrapper = async (category) => {
    if (!checkPermission("admin", "update this category")) return;

    // 🎨 Use the shared validator for descriptive feedback
    const errors = validateCategory(category, true);

    if (Object.keys(errors).length > 0) {
      Object.entries(errors).forEach(([field, msg]) => {
        showNotification({
          type: "error",
          title: "Field Required",
          message: msg,
        });
      });
      return;
    }

    try {
      const updatedRaw = await AdminCategoryService.update(category);
      const updatedCategory = normalizeCategory(updatedRaw);
      console.log(updatedCategory)

      setCategories((prev) =>
        prev.map((c) =>
          c.id === updatedCategory.id
            ? { ...c, ...updatedCategory }
            : c
        )
      );

      updateUnitMeasurementsState([updatedCategory.unitMeasurement]);

      showNotification({
        type: "success",
        title: "Category Updated",
        message: `"${updatedCategory.name}" has been updated successfully.`,
      });

      setShowEditModal(false);
      setEditingCategory(null);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        "Failed to update category";

      showNotification({
        type: "error",
        title: "Update Failed",
        message: message,
      });

      console.error("Failed to update category:", err);
    }
  };



  // Toggle status
  const handleToggleStatus = async (categoryId) => {
    if (!checkPermission("admin", "toggle category status")) return;

    setLoadingStatus((prev) => ({ ...prev, [categoryId]: true }));
    try {
      const updatedRaw = await AdminCategoryService.toggleStatus(categoryId);
      const updatedCategory = normalizeCategory(updatedRaw);

      setCategories((prev) =>
        prev.map((c) =>
          c.id === updatedCategory.id ? { ...c, ...updatedCategory } : c
        )
      );

      showNotification({
        type: "success",
        title: "Status Updated",
        message: `Category "${updatedCategory.name}" is now ${updatedCategory.status}.`,
      });
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to toggle status";
      showNotification({
        type: "error",
        title: "Toggle Failed",
        message: message,
      });
      console.error(
        "Failed to toggle status:",
        err?.response?.data || err
      );
    } finally {
      setLoadingStatus((prev) => ({ ...prev, [categoryId]: false }));
    }
  };


  // --- Delete / Disable Handlers ---
  const handleDeleteClick = async (category) => {
    if (!checkPermission("admin", "delete this category")) return;

    if (!category?.id) return;

    const normalized = normalizeCategory(category);

    // If category has products → show disable confirmation
    if (normalized.productsCount > 0) {
      setShowDeleteConfirm(normalized);
      return;
    }

    setLoadingDelete((prev) => ({ ...prev, [normalized.id]: true }));
    // Safe to hard-delete (no products)
    try {
      await AdminCategoryService.delete(normalized.id);

      setCategories((prev) =>
        prev.filter((c) => c.id !== normalized.id)
      );

      showNotification({
        type: "success",
        title: "Category Deleted",
        message: `"${normalized.name}" has been deleted successfully.`,
      });
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        "Failed to delete category";

      showNotification({
        type: "error",
        title: "Delete Failed",
        message: message,
      });

      console.error("Failed to delete category:", err);
    } finally {
      setLoadingDelete((prev) => ({ ...prev, [normalized.id]: false }));
    }
  };

  const handleDisableInstead = async () => {
    if (!showDeleteConfirm) return;

    try {
      const updatedRaw = await AdminCategoryService.toggleStatus(
        showDeleteConfirm.id
      );
      const updatedCategory = normalizeCategory(updatedRaw);

      setCategories((prev) =>
        prev.map((c) =>
          c.id === updatedCategory.id ? { ...c, ...updatedCategory } : c
        )
      );

      showNotification({
        type: "success",
        title: "Status Updated",
        message: `Category "${updatedCategory.name}" is now ${updatedCategory.status}.`,
      });
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to disable category";
      showNotification({
        type: "error",
        title: "Toggle Failed",
        message: message,
      });
      console.error(
        "Failed to disable category:",
        err?.response?.data || err
      );
    } finally {
      setShowDeleteConfirm(null);
    }
  };

  // --- Bulk Actions Override for Delete ---
  const handleBulkActionWrapper = async (action) => {
    if (!checkPermission("admin", "perform bulk actions")) return;

    try {
      if (action === "delete") {
        if (selectedCategories.length === 0) return;

        // Show modal instead of deleting directly
        const categoriesToDisable = categories.filter((c) =>
          selectedCategories.includes(c.id)
        );

        // Set the first one to trigger the modal (UI shows confirmation)
        setShowDeleteConfirm(categoriesToDisable[0]);
      } else if (action === "toggle-status") {
        const updatesRaw = await Promise.all(
          selectedCategories.map((id) =>
            AdminCategoryService.toggleStatus(id)
          )
        );

        const updates = updatesRaw.map(normalizeCategory);

        setCategories((prev) =>
          prev.map((c) => {
            const updated = updates.find((u) => u.id === c.id);
            return updated ? { ...c, ...updated } : c;
          })
        );

        setSelectedCategories([]);
      }
    } catch (err) {
      console.error("Bulk action failed:", err);
    }
  };






  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowEditModal(true);
  };

  const handleEditClose = () => {
    setShowEditModal(false);
    setEditingCategory(null);
  };



  return (
    <AdminLayout>
      <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
        <div className="mb-6">
          <PageHeader onAddCategory={() => setShowAddModal(true)} />

          <FilterBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />

          <BulkActionsBar
            selectedCount={selectedCategories.length}
            onBulkAction={handleBulkActionWrapper}
          />
        </div>

        {isLoading ? (
          <LoadingSkeleton />
        ) : filteredCategories.length === 0 ? (
          <EmptyState onAddCategory={() => setShowAddModal(true)} />
        ) : (
          <CategoriesTable
            categories={filteredCategories}
            selectedCategories={selectedCategories}
            allSelected={allSelected}
            onSelectAll={(checked) =>
              handleSelectAll(checked, filteredCategories)
            }
            onSelectCategory={handleSelectCategory}
            onEdit={handleEdit}
            onToggleStatus={handleToggleStatus}
            onDelete={handleDeleteClick}
            loadingStatus={loadingStatus}
            loadingDelete={loadingDelete}
          />
        )}

        {showAddModal && (
          <AddCategoryModal
            newCategories={newCategories}
            setNewCategories={setNewCategories}
            handleImageUpload={handleImageUpload}
            addAnotherCategory={addAnotherCategory}
            removeCategory={removeCategory}
            handleAddCategory={handleAddCategorySubmit}
            setShowAddModal={setShowAddModal}
            unitMeasurements={unitMeasurements}
          />
        )}

        {showEditModal && editingCategory && (
          <EditCategoryModal
            isOpen={showEditModal}
            category={editingCategory}
            onClose={handleEditClose}
            onSave={handleEditSaveWrapper}
            unitMeasurements={unitMeasurements}
          />
        )}

        <DeleteConfirmModal
          category={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(null)}
          onDisableInstead={handleDisableInstead}
        />
      </div>
    </AdminLayout>
  );
}






